import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { Box, ButtonLink, Heading, Stack, Text } from '@eneco/sparky';

import type { CtaBlockButton, CtaBlockContent } from './cta-block-content.types';

/**
 * `ctaBlock` block for the CMS-agnostic lane. POC-schema-only (see `cta-block-content.types.ts`).
 * A button shows only when its link has a URL and the button has a label.
 */
const CtaBlock: FC = () => {
  const content = useContent<CtaBlockContent>();
  const buttons = content.buttons ?? [];

  if (!content.title && !content.description && buttons.length === 0) return null;

  return (
    <Box backgroundColor="backgroundBrand" padding="10" borderRadius="m">
      <Stack gap="6" alignX="center" alignY="center">
        {content.title && <Heading as="h2">{content.title}</Heading>}
        {content.description && <Text>{content.description}</Text>}
        {buttons.length > 0 && (
          <Stack direction="row" gap="4">
            {buttons.map(button => (
              <CtaButton key={button._key} button={button} />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const CtaButton: FC<{ button: CtaBlockButton }> = ({ button }) => {
  const link = linkWithUrl(button.link);
  if (!link || !button.label) return null;

  return (
    <ButtonLink href={link.href} target={link.target} action={button.variant ?? 'primary'}>
      {button.label}
    </ButtonLink>
  );
};

export default CtaBlock;
